import {
  Component,
  effect,
  untracked,
  input,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LiteralService, TownsService } from 'src/app/services';
import { images } from 'src/images';
import { ICustomToggleOptions, IBase, IRestrictions } from 'src/app/models';
import { CustomToggleTone, RestrictionsTarget, RestrictionsType } from 'src/app/models/enums';
import { APP } from 'src/app/config/app';

@Component({
  selector: 'app-restrictions-search-bar',
  templateUrl: './restrictions-search-bar.component.html',
  styleUrl: './restrictions-search-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class RestrictionsSearchBarComponent {
  readonly restrictionsType = input(RestrictionsType.BLACK_LIST);
  readonly restrictionsTarget = input(RestrictionsTarget.STOPS);
  readonly stopOptions = input<{ value: number; label: string }[]>([]);
  readonly townId = input<number>();
  readonly routeId = input<number>();
  readonly bulkSelectMode = input(false);
  readonly selectedCount = input(0);
  readonly pageSize = input(APP.DEFAULT_PAGE_SIZE);
  readonly existingRestrictions = input<IRestrictions[]>([]);
  readonly whiteListDraftMode = input(false);
  readonly whiteListDraftSize = input(0);
  readonly backClicked = output();
  readonly restrictionAdded = output<{
    origin: IBase;
    destinations: IBase[];
  }>();
  readonly changeRestrictionTypeEventEmitter = output<RestrictionsType>();
  readonly searchEventEmitter = output<string>();
  readonly bulkDeleteClicked = output();
  readonly cancelBulkClicked = output();
  readonly confirmBulkDeleteClicked = output();
  readonly pageSizeChanged = output<number>();
  readonly confirmWhiteListDraftClicked = output();
  readonly cancelWhiteListDraftClicked = output();

  public destinationOptions: IBase[] = [];
  public selectedOriginId: number | null = null;
  public selectedOrigin: IBase | null = null;
  public selectedDestinations: IBase[] = [];
  public searchValue = '';
  public rowsDisplayOpened = false;
  public readonly defaultPageSize = APP.DEFAULT_PAGE_SIZE;
  public images = images;

  public RestrictionsTarget = RestrictionsTarget;
  public readonly restrictionsTypeToggleOptions: ICustomToggleOptions<RestrictionsType> = [
    {
      value: RestrictionsType.BLACK_LIST,
      image: images.services.restrictions.blacklist,
      descriptionKey: 'services.routes.restrictions.blacklist',
      tone: CustomToggleTone.DANGER,
    },
    {
      value: RestrictionsType.WHITE_LIST,
      image: images.services.restrictions.whitelist,
      descriptionKey: 'services.routes.restrictions.whitelist',
      tone: CustomToggleTone.SUCCESS,
    },
  ];

  constructor(
    public literalService: LiteralService,
    private townsService: TownsService,
  ) {
    effect(() => {
      if (!this.selectedOriginId) {
        this.destinationOptions = this.buildDestinationOptions();
      }
    });
    effect(() => {
      this.restrictionsTarget();
      untracked(() => this.resetSelection());
    });
  }

  private get isAreaTarget(): boolean {
    return this.restrictionsTarget() === RestrictionsTarget.AREAS;
  }

  private resetSelection(): void {
    this.selectedOriginId = null;
    this.selectedOrigin = null;
    this.selectedDestinations = [];
    this.searchValue = '';
    this.destinationOptions = this.buildDestinationOptions();
  }

  private buildDestinationOptions(): IBase[] {
    return this.stopOptions()
      .filter((s) => this.isAreaTarget || s.value !== this.selectedOriginId)
      .map((s) => ({ id: s.value, name: s.label }));
  }

  async onOriginSelect(event: any): Promise<void> {
    this.selectedOriginId = event?.value ?? null;
    this.selectedOrigin = this.selectedOriginId
      ? {
          id: this.selectedOriginId,
          name: this.stopOptions().find(
            (s) => s.value === this.selectedOriginId,
          )?.label,
        }
      : null;
    this.selectedDestinations = this.selectedDestinations.filter(
      (d) => this.isAreaTarget || d.id !== this.selectedOriginId,
    );

    const townId = this.townId();
    if (!this.selectedOriginId || !townId || this.isAreaTarget) {
      this.destinationOptions = this.buildDestinationOptions();
      return;
    }

    const originId = this.selectedOriginId;
    const stops = (await this.townsService.getStopsFrom(
      townId,
      originId,
      undefined,
      false,
      this.routeId(),
      undefined,
      undefined,
      false,
    )) as any[];
    const listedDestinationIds = this.getListedDestinationIds(originId);
    this.destinationOptions = stops
      .filter((s: any) => !listedDestinationIds.has(s.id))
      .map((s: any) => ({
        id: s.id,
        name: s.name ?? s.code ?? String(s.id),
      }))
      .sort((first, second) => APP.RESTRICTIONS_NAME_COLLATOR.compare(first.name, second.name));
    this.selectedDestinations = this.selectedDestinations.filter((d) =>
      this.destinationOptions.some((o) => o.id === d.id),
    );
  }

  private getListedDestinationIds(originId: number): Set<number | undefined> {
    const listedRestriction = this.existingRestrictions().find(restriction => restriction.originStop.id === originId);
    return new Set((listedRestriction?.restrictedDestinations ?? []).map(restrictedDestination => restrictedDestination.destination.id));
  }

  addRestriction(): void {
    if (!this.selectedOrigin || this.selectedDestinations.length === 0) return;
    this.restrictionAdded.emit({
      origin: this.selectedOrigin,
      destinations: this.selectedDestinations,
    });
    this.resetSelection();
  }

  changeRestrictionType(requestedType: unknown): void {
    this.changeRestrictionTypeEventEmitter.emit(requestedType as RestrictionsType);
  }

  onConfirmWhiteListDraftClick(): void {
    this.confirmWhiteListDraftClicked.emit();
  }

  onCancelWhiteListDraftClick(): void {
    this.cancelWhiteListDraftClicked.emit();
  }

  goBack(): void {
    this.backClicked.emit();
  }

  search(value: string): void {
    this.searchValue = value;
    this.searchEventEmitter.emit(value);
  }

  onBulkDeleteClick(): void {
    this.bulkDeleteClicked.emit();
  }

  onCancelBulkClick(): void {
    this.cancelBulkClicked.emit();
  }

  onConfirmBulkDeleteClick(): void {
    this.confirmBulkDeleteClicked.emit();
  }

  changePageSize(size: number): void {
    this.rowsDisplayOpened = false;
    this.pageSizeChanged.emit(size);
  }
}
